import { useQuery } from '@tanstack/react-query'

import { getAuthConfigs, getToolkits } from '~/generated/client'
import { Duration } from '~/utils/duration'

export const QUERY_KEYS = {
  GET_TOOLS: 'GET_TOOLS',
  getAppListQueryKey: (page: number, category?: string | null) =>
    ['GET_TOOLS', page, category] as const,
  GET_INTEGRATIONS: 'GET_INTEGRATIONS',
  getIntegrationQueryKey: (appName: string | null = null) =>
    ['GET_INTEGRATIONS', appName] as const,
}

export const useGetAllToolkits = ({
  page,
  category,
  suspense = false,
}: {
  page: number
  category?: string | null
  suspense?: boolean
}) => {
  const {
    data: { items: toolkits = [], total_pages = 1 } = {},
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useQuery(
    QUERY_KEYS.getAppListQueryKey(page, category),
    async () => {
      return getToolkits({
        category: category ?? undefined,
        managedBy: 'all',
      })
    },
    {
      retry: false,
      staleTime: Duration({ min: 5 }).toMS(),
      suspense: suspense,
    },
  )

  const getLogo = (slug: string) => {
    return toolkits.find((toolkit) => toolkit.slug === slug)?.meta?.logo
  }

  return {
    data: {
      items: toolkits,
      totalPages: total_pages,
    },
    isLoading,
    isError,
    isSuccess,
    refetch,
    getLogo,
  }
}

// Integrations Page
export const useGetAllIntegrations = ({
  toolkitSlug,
  page: _ = 1,
}: {
  toolkitSlug: string | undefined
  page: number
}) => {
  const { data, isLoading, isError, isSuccess, refetch } = useQuery({
    queryKey: QUERY_KEYS.getIntegrationQueryKey(toolkitSlug),
    queryFn: () =>
      getAuthConfigs({
        limit: 2000,
        toolkitSlug: toolkitSlug,
      }),
    enabled: !!toolkitSlug,
    staleTime: Duration({ d: 1 }).toMS(),
    retry: true,
    retryOnMount: false,
    refetchOnMount: false,
  })

  return {
    data,
    isLoading,
    isError,
    isSuccess,
    refetch,
  }
}
